/**
 * Legend swatches for the radar tiles.
 *
 * Three things this deliberately is not:
 *
 * **It is not quantitative.** No dBZ numbers, no mm/h. RainViewer composites
 * national radar networks whose reflectivity scaling differs from country to
 * country, so a number against a colour on a global mosaic would claim a
 * precision the data doesn't have. The scale runs light → intense, which is the
 * question a reader actually asks of a radar map.
 *
 * **It is not a choice.** There was a picker over RainViewer's eight documented
 * colour schemes until it turned out they all return the same tile — see
 * `RAINVIEWER_COLOR_SCHEME`. One palette renders, so one ramp is described.
 *
 * **It is not guesswork.** The stops below were read out of a real tile rather
 * than matched by eye: fetch a frame with smoothing off (`0_0`, which gives
 * RainViewer's bands instead of an interpolated gradient), decode it, and take
 * the distinct opaque colours. That sampling found 39 of them forming one
 * continuous progression — pale cyan deepening to navy, then a hard jump to
 * yellow and down through orange to a dark red. The eight stops here are
 * spaced along it. There is no magenta at the top end; an earlier hand-matched
 * ramp claimed one, along with a green low end that does not exist either.
 *
 * If the tiles ever visibly disagree with these swatches, re-sample and fix the
 * ramp here — the tiles are the truth.
 */

export interface RadarPalette {
  /** Increasing intensity, light → intense. */
  rain: readonly string[];
  /**
   * Frozen precipitation, which RainViewer renders in its own palette when
   * `showSnow` is on. Shown separately because snow and heavy rain otherwise
   * occupy confusingly similar positions on the ramp.
   */
  snow: readonly string[];
}

/**
 * Sampled from RainViewer's own tiles. The jump from `#004768` to `#ffee00` is
 * theirs, not a gap in the sampling — the palette really does step from the
 * darkest blue straight into yellow.
 */
const RAIN_RAMP = [
  '#88ddee',
  '#00a3e0',
  '#0077aa',
  '#004768',
  '#ffee00',
  '#ffaa00',
  '#ff4400',
  '#a80000',
] as const;

const SNOW_RAMP = ['#e9f3ff', '#c3ddf5', '#9dc4e8', '#7aa8d8'] as const;

/** The three points on the ramp worth naming. */
export const LEGEND_LABELS = ['Light', 'Moderate', 'Intense'] as const;

export const RADAR_PALETTE: RadarPalette = { rain: RAIN_RAMP, snow: SNOW_RAMP };

/**
 * A CSS gradient of the ramp, for the places a continuous bar reads better than
 * discrete swatches — the track behind the opacity slider, for one.
 */
export function rampGradient(): string {
  return `linear-gradient(to right, ${RADAR_PALETTE.rain.join(', ')})`;
}
